<template>
  <div>
    <Layout ref="layoutRef" :selectedOption="3" />
    <div class="d-flex align-items-center justify-content-between">
      <h4 class="mb-0">
        Cargar e indexar PDFs <span class="text-muted fs-6">— Trámite N° {{ idTramite ?? "—" }}</span>
      </h4>
      <button type="button" class="btn btn-outline-secondary btn-sm" @click="volver">
        <span class="material-icons me-1" style="font-size:16px;vertical-align:middle">arrow_back</span>
        Volver
      </button>
    </div>
    <hr />

    <div v-if="!idTramite" class="alert alert-danger">
      No se indicó el número de trámite. Vuelva a la lista de trámites y abra esta pantalla desde allí.
    </div>

    <template v-else>

    <!-- ── PASO 1: Subir carpeta ─────────────────────────────────────── -->
    <div class="card shadow-sm mb-3" :class="{ 'border-success': done }">
      <div class="card-body">
        <div class="d-flex align-items-center gap-2 mb-3">
          <span class="badge rounded-pill" :class="done ? 'bg-success' : 'bg-primary'">1</span>
          <h5 class="mb-0 flex-grow-1">Subir carpeta</h5>
          <span v-if="done" class="badge bg-success">Completado</span>
        </div>

        <!-- Zona de carga -->
        <div
          class="border border-2 rounded p-4 text-center mb-3"
          :class="zonaClase"
          style="cursor:pointer; border-style:dashed !important"
          @dragover.prevent="dragging = true"
          @dragleave.prevent="dragging = false"
          @drop.prevent="onDrop"
          @click="!uploading && $refs.inputCarpeta.click()"
        >
          <input ref="inputCarpeta" type="file" webkitdirectory multiple class="d-none" @change="onInput" />
          <span class="material-icons" style="font-size:40px">{{ files.length ? "folder" : "drive_folder_upload" }}</span>
          <template v-if="!files.length">
            <div><strong>Arrastre la carpeta <code>{{ idTramite }}</code> aquí</strong></div>
            <div class="text-muted small">o haga clic para seleccionarla</div>
          </template>
          <template v-else>
            <div><strong>{{ folderName }}</strong></div>
            <div class="text-muted small">{{ files.length }} archivos · {{ totalSize }}</div>
          </template>
        </div>

        <!-- Resumen de la carpeta -->
        <div v-if="files.length" class="mb-3">
          <div class="d-flex flex-wrap gap-1 mb-2">
            <span class="badge bg-secondary">{{ files.length }} archivos</span>
            <span class="badge" :class="totalPdfs ? 'bg-info text-dark' : 'bg-warning text-dark'">
              {{ totalPdfs }} {{ totalPdfs === 1 ? "PDF" : "PDFs" }}
            </span>
            <span v-if="ignorados" class="badge bg-light text-dark border" title="Archivos del sistema omitidos (.DS_Store, Thumbs.db, temporales)">
              {{ ignorados }} omitidos
            </span>
          </div>
          <div v-if="!totalPdfs" class="alert alert-warning py-2 small mb-2">
            La carpeta no contiene archivos PDF: no habrá nada que indexar.
          </div>
          <details class="small">
            <summary class="text-muted">Ver archivos</summary>
            <div class="table-responsive mt-2" style="max-height:220px; overflow-y:auto">
              <table class="table table-sm table-hover mb-0">
                <tbody>
                  <tr v-for="f in archivosVisibles" :key="rutaDe(f)">
                    <td class="text-nowrap">{{ rutaDe(f) }}</td>
                    <td class="text-end text-nowrap text-muted">{{ formatBytes(f.size) }}</td>
                  </tr>
                  <tr v-if="files.length > archivosVisibles.length">
                    <td colspan="2" class="text-muted text-center">
                      … y {{ files.length - archivosVisibles.length }} más
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>
        </div>

        <!-- Progreso -->
        <div v-if="uploading || done || uploadedBytes > 0" class="mb-3">
          <div class="progress" style="height:10px">
            <div
              class="progress-bar"
              :class="done ? 'bg-success' : 'progress-bar-striped progress-bar-animated'"
              role="progressbar"
              :style="{ width: globalProgress + '%' }"
              :aria-valuenow="globalProgress"
              aria-valuemin="0"
              aria-valuemax="100"
            ></div>
          </div>
          <div class="d-flex justify-content-between small text-muted mt-1">
            <span>{{ globalProgress }}% · {{ formatBytes(uploadedBytes) }} de {{ totalSize }}</span>
            <span>{{ uploadedCount }}/{{ files.length }} archivos</span>
            <span v-if="speed">{{ speed }}</span>
          </div>
        </div>

        <p v-if="currentFile && uploading" class="small text-muted text-truncate mb-3">
          <span class="material-icons" style="font-size:14px;vertical-align:middle">upload</span>
          {{ currentFile }}
        </p>

        <div v-if="paused" class="alert alert-warning py-2">
          <span class="spinner-border spinner-border-sm me-1"></span>
          Conexión interrumpida — reintentando automáticamente...
        </div>
        <div v-if="uploadError" class="alert alert-danger py-2">{{ uploadError }}</div>
        <div v-if="done" class="alert alert-success py-2">Carpeta subida correctamente.</div>

        <div class="d-flex gap-2">
          <button
            v-if="files.length && !uploading && !done"
            type="button"
            class="btn btn-primary"
            @click="startUpload"
          >
            <span class="material-icons me-1" style="font-size:18px;vertical-align:middle">cloud_upload</span>
            {{ textoSubir }}
          </button>
          <button v-if="uploading" type="button" class="btn btn-outline-danger" @click="cancelUpload">
            Cancelar
          </button>
          <button
            v-if="files.length && !uploading"
            type="button"
            class="btn btn-secondary"
            :disabled="ejecutando || importando"
            @click="reset"
          >
            {{ done ? "Subir otra" : "Limpiar" }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── PASO 2: Indexar PDFs ──────────────────────────────────────── -->
    <div class="card shadow-sm mb-3" :class="[{ bloqueado: !done }, estadoFinal === 'COMPLETADO' ? 'border-success' : '']">
      <div class="card-body">
        <div class="d-flex align-items-center gap-2 mb-3">
          <span class="badge rounded-pill" :class="done ? 'bg-primary' : 'bg-secondary'">2</span>
          <h5 class="mb-0 flex-grow-1">Indexar PDFs</h5>
          <span v-if="!done" class="badge bg-light text-muted border">Esperando subida</span>
          <span v-else-if="ejecutando" class="badge bg-info text-dark">Indexando…</span>
          <span v-else-if="estadoFinal === 'COMPLETADO'" class="badge bg-success">Completado</span>
          <span v-else-if="estadoFinal === 'ERROR'" class="badge bg-danger">Error</span>
        </div>

        <p class="text-muted mb-3">
          Trámite N°: <strong>{{ idTramite ?? "—" }}</strong>
        </p>

        <button
          type="button"
          class="btn btn-primary mb-3"
          :disabled="!done || ejecutando"
          @click="ejecutarIndexador"
        >
          <span v-if="ejecutando" class="spinner-border spinner-border-sm me-1"></span>
          <span v-else class="material-icons me-1" style="font-size:18px;vertical-align:middle">manage_search</span>
          {{ ejecutando ? "Indexando..." : estadoFinal ? "Indexar de nuevo" : "Indexar PDFs" }}
        </button>

        <div v-if="lineas.length" ref="consolaRef" class="consola mb-3">
          <p v-for="(linea, i) in lineas" :key="i" :class="claseLinea(linea)">{{ linea }}</p>
        </div>

        <div
          v-if="estadoFinal"
          class="alert py-2 mb-0"
          :class="estadoFinal === 'COMPLETADO' ? 'alert-success' : 'alert-danger'"
        >
          {{ estadoFinal === "COMPLETADO" ? "Indexación completada." : "Error en la indexación." }}
        </div>
      </div>
    </div>

    <!-- ── PASO 3: Importar en la base de datos ──────────────────────── -->
    <div
      class="card shadow-sm mb-3"
      :class="[{ bloqueado: estadoFinal !== 'COMPLETADO' }, estadoImport === 'COMPLETADO' ? 'border-success' : '']"
    >
      <div class="card-body">
        <div class="d-flex align-items-center gap-2 mb-3">
          <span class="badge rounded-pill" :class="estadoFinal === 'COMPLETADO' ? 'bg-primary' : 'bg-secondary'">3</span>
          <h5 class="mb-0 flex-grow-1">Importar en la base de datos</h5>
          <span v-if="estadoFinal !== 'COMPLETADO'" class="badge bg-light text-muted border">Esperando indexación</span>
          <span v-else-if="importando" class="badge bg-info text-dark">Importando…</span>
          <span v-else-if="estadoImport === 'COMPLETADO'" class="badge bg-success">Completado</span>
          <span v-else-if="estadoImport === 'ERROR'" class="badge bg-danger">Error</span>
        </div>

        <p class="text-muted mb-3">
          Carga el archivo <code>{{ idTramite }}_index.csv</code> generado en el paso 2: relaciona las
          rutas de los archivos con las atenciones médicas
          (<code>carga_importar_archivo_pla({{ idTramite }}, 0)</code>).
        </p>

        <button
          type="button"
          class="btn btn-primary mb-3"
          :disabled="estadoFinal !== 'COMPLETADO' || importando"
          @click="ejecutarImportacion"
        >
          <span v-if="importando" class="spinner-border spinner-border-sm me-1"></span>
          <span v-else class="material-icons me-1" style="font-size:18px;vertical-align:middle">database</span>
          {{ importando ? "Importando..." : estadoImport ? "Importar de nuevo" : "Importar en la base de datos" }}
        </button>

        <div v-if="errorImport" class="alert alert-danger py-2">{{ errorImport }}</div>

        <!-- Resumen devuelto por el procedimiento -->
        <template v-if="resumenImport.length">
          <h6 class="fw-bold">Resumen de importación</h6>
          <div v-for="(a, i) in advertenciasImport" :key="i" class="alert alert-warning py-2 small">{{ a }}</div>
          <div class="table-responsive">
            <table class="table table-sm table-bordered align-middle mb-0">
              <tbody>
                <tr v-for="r in resumenImport" :key="r.campo" :class="claseResumen(r)">
                  <th class="fw-normal text-nowrap" style="width:40%">{{ r.campo }}</th>
                  <td class="text-break">{{ r.valor }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
    </div>
    </template>

    <Footer />
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";
import { useRoute, useRouter, onBeforeRouteLeave } from "vue-router";
import Swal from "sweetalert2";
import Layout from "@/components/Layout.vue";
import Footer from "@/components/Footer.vue";
import api from "@/services/api";

// ── Constantes ────────────────────────────────────────────────────────────────
const CHUNK_SIZE   = 5 * 1024 * 1024;
const TIMEOUT_CHUNK_MS = 5 * 60 * 1000; // un chunk de 5 MB en una red lenta no debe quedar colgado para siempre
const CONCURRENCY  = 2;
const MAX_RETRIES  = 5;
const RETRY_DELAYS = [1000, 3000, 6000, 12000, 24000];
const MAX_LINEAS   = 5000; // líneas que conserva la consola del indexador
const MAX_LISTADO  = 100;  // archivos que se listan en el detalle de la carpeta

// Archivos del sistema que no deben subirse
const BASURA = /(^|\/)(\.DS_Store|Thumbs\.db|desktop\.ini|~\$[^/]*)$/i;

// ── Ruta ──────────────────────────────────────────────────────────────────────
const route     = useRoute();
const router    = useRouter();
const idTramite = computed(() => route.params.p_idtramite);

// Si se abre otro trámite sin desmontar la pantalla, se empieza de cero
watch(idTramite, () => reset());

// Volver a la pantalla desde la que se abrió (la lista de trámites)
function volver() {
  router.back();
}

// Ya no es un modal: se puede salir con el menú o el botón Atrás. Si hay una subida o una
// indexación en curso, se pide confirmación antes de abandonar la pantalla.
onBeforeRouteLeave(async () => {
  if (!uploading.value && !ejecutando.value && !importando.value) return true;
  const r = await Swal.fire({
    icon: "warning",
    title: uploading.value ? "Subida en curso" : ejecutando.value ? "Indexación en curso" : "Importación en curso",
    text: uploading.value
      ? "Si sale ahora se interrumpe la subida (podrá reanudarla después)."
      : ejecutando.value
        ? "Si sale ahora se pierde el seguimiento de la indexación."
        : "La importación sigue ejecutándose en el servidor aunque salga, pero no verá el resumen.",
    showCancelButton: true,
    confirmButtonText: "Salir",
    cancelButtonText: "Quedarme",
  });
  return r.isConfirmed;
});

// ── Layout ────────────────────────────────────────────────────────────────────
const layoutRef = ref(null);
const inputCarpeta = ref(null);
const consolaRef = ref(null);

// ── Estado: Uploader ──────────────────────────────────────────────────────────
const files         = ref([]);
const ignorados     = ref(0);
const uploading     = ref(false);
const done          = ref(false);
const paused        = ref(false);
const cancelled     = ref(false);
const currentFile   = ref("");
const uploadedCount = ref(0);
const uploadedBytes = ref(0);
const speed         = ref("");
const uploadError   = ref("");
const dragging      = ref(false);

let startTime    = 0;
let sessionBytes = 0;     // bytes enviados en esta sesión (para la velocidad)
let abortador    = null;  // AbortController de la subida en curso

// ── Estado: Indexador ─────────────────────────────────────────────────────────
const lineas      = ref([]);
const ejecutando  = ref(false);
const estadoFinal = ref(null);
let   indexAbortador = null; // AbortController del flujo SSE del indexador

// ── Estado: Importación en la base de datos (paso 3) ──────────────────────────
const importando    = ref(false);
const estadoImport  = ref(null);  // null | "COMPLETADO" | "ERROR"
const resumenImport = ref([]);    // [{ campo, valor }] tomado de los RAISE NOTICE del procedimiento
const errorImport   = ref("");

const valorImport = campo => {
  const fila = resumenImport.value.find(r => r.campo === campo);
  return fila ? Number(String(fila.valor).replace(/[^\d.-]/g, "")) : null;
};

const advertenciasImport = computed(() => {
  const avisos = [];
  const sinMatch = valorImport("Sin match planilla");
  const sinPlanilla = valorImport("tiene_planilla = 0");
  if (sinMatch > 0) {
    avisos.push(`${sinMatch} filas del CSV no coincidieron con ninguna planilla.`);
  }
  if (sinPlanilla > 0) {
    avisos.push(`${sinPlanilla} atenciones quedaron sin planilla (tiene_planilla = 0).`);
  }
  return avisos;
});

// Resalta las filas del resumen que indican algo sin relacionar
function claseResumen(r) {
  const alerta = r.campo === "Sin match planilla" || r.campo === "tiene_planilla = 0";
  return alerta && Number(String(r.valor).replace(/[^\d.-]/g, "")) > 0 ? "table-warning" : "";
}

// El handler de BusinessException puede responder texto plano o JSON con distintas claves
function mensajeDeError(error) {
  const data = error.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  const m = data?.mensaje || data?.message || data?.error || data?.detail;
  if (m) return String(m);
  return error.response
    ? `Error del servidor (HTTP ${error.response.status}).`
    : "No se pudo conectar con el servidor.";
}

function reiniciarImportacion() {
  importando.value    = false;
  estadoImport.value  = null;
  resumenImport.value = [];
  errorImport.value   = "";
}

// ── Ciclo de vida ─────────────────────────────────────────────────────────────
onMounted(() => {
  layoutRef.value?.toggleProcesos();
  layoutRef.value?.toggleReportes();
  layoutRef.value?.toggleParametros();
  layoutRef.value?.toggleAyuda();
  window.addEventListener("beforeunload", avisarSalida);
});

onUnmounted(() => {
  window.removeEventListener("beforeunload", avisarSalida);
  abortador?.abort();
  indexAbortador?.abort();
});

// Avisa antes de cerrar la pestaña si hay una subida o indexación en curso
function avisarSalida(e) {
  if (uploading.value || ejecutando.value || importando.value) {
    e.preventDefault();
    e.returnValue = "";
  }
}

// ── Computed ──────────────────────────────────────────────────────────────────
const totalBytes = computed(() => files.value.reduce((s, f) => s + f.size, 0));
const totalSize  = computed(() => formatBytes(totalBytes.value));
const totalPdfs  = computed(() => files.value.filter(f => /\.pdf$/i.test(f.name)).length);

const folderName = computed(() => rutaDe(files.value[0] || {}).split("/")[0] || "Carpeta");

const archivosVisibles = computed(() => files.value.slice(0, MAX_LISTADO));

const globalProgress = computed(() => {
  if (done.value) return 100;
  if (!totalBytes.value) return 0;
  return Math.min(100, Math.round((uploadedBytes.value / totalBytes.value) * 100));
});

const textoSubir = computed(() => (uploadedBytes.value > 0 ? "Reanudar subida" : "Subir carpeta"));

const zonaClase = computed(() => {
  if (dragging.value) return "border-primary bg-light";
  if (files.value.length) return "border-success bg-light";
  return "border-secondary-subtle";
});

// ── Selección de carpeta ──────────────────────────────────────────────────────
function rutaDe(f) {
  return f.relativePath || f.webkitRelativePath || f.name || "";
}

// Quita los archivos del sistema y fija la ruta relativa de cada archivo
function prepararArchivos(lista) {
  const validos = [];
  let omitidos = 0;
  lista.forEach(f => {
    if (!f.relativePath) f.relativePath = f.webkitRelativePath || f.name;
    if (BASURA.test(f.relativePath)) omitidos++;
    else validos.push(f);
  });
  return { validos, omitidos };
}

// Toda la selección debe estar dentro de una carpeta que se llame igual que el trámite
function validarNombreCarpeta(archivos) {
  if (!archivos.length) return false;
  const esperado = String(idTramite.value);
  const raices = [...new Set(archivos.map(f => rutaDe(f).split("/")[0]))];

  if (raices.length === 1 && raices[0] === esperado) return true;

  Swal.fire({
    icon: "warning",
    title: "Carpeta incorrecta",
    html: `La carpeta debe llamarse <strong>${escapar(esperado)}</strong>.<br>
           Se seleccionó: <strong>${raices.map(escapar).join(", ")}</strong>.`,
    confirmButtonText: "Entendido",
  });
  return false;
}

function aceptarSeleccion(lista) {
  if (uploading.value) return;
  const { validos, omitidos } = prepararArchivos(lista);
  if (!validos.length) {
    Swal.fire({ icon: "warning", title: "Carpeta vacía", text: "No se encontraron archivos para subir." });
    return;
  }
  if (!validarNombreCarpeta(validos)) return;
  reset();
  files.value = validos;
  ignorados.value = omitidos;
}

function onInput(e) {
  aceptarSeleccion([...e.target.files]);
  e.target.value = ""; // permite volver a elegir la misma carpeta
}

async function onDrop(e) {
  dragging.value = false;
  if (uploading.value) return;
  // Las entradas deben tomarse ANTES de cualquier await: después el navegador vacía la lista
  const entradas = [...e.dataTransfer.items].map(i => i.webkitGetAsEntry?.()).filter(Boolean);
  try {
    const todos = [];
    for (const entrada of entradas) todos.push(...(await recorrer(entrada)));
    aceptarSeleccion(todos);
  } catch (err) {
    Swal.fire({ icon: "error", title: "No se pudo leer la carpeta", text: err.message });
  }
}

// readEntries devuelve las entradas por lotes (máx. ~100): hay que repetir hasta que venga vacío
async function leerEntradas(reader) {
  const todas = [];
  for (;;) {
    const lote = await new Promise((res, rej) => reader.readEntries(res, rej));
    if (!lote.length) return todas;
    todas.push(...lote);
  }
}

async function recorrer(entry, path = "") {
  if (entry.isFile) {
    const f = await new Promise((res, rej) => entry.file(res, rej));
    f.relativePath = path + f.name;
    return [f];
  }
  const hijos  = await leerEntradas(entry.createReader());
  const nested = await Promise.all(hijos.map(h => recorrer(h, path + entry.name + "/")));
  return nested.flat();
}

// ── Upload principal ──────────────────────────────────────────────────────────
async function startUpload() {
  uploading.value     = true;
  done.value          = false;
  cancelled.value     = false;
  paused.value        = false;
  uploadError.value   = "";
  uploadedCount.value = 0;
  uploadedBytes.value = 0;
  speed.value         = "";
  sessionBytes        = 0;
  startTime           = Date.now();
  abortador           = new AbortController();

  const cola = [...files.value];
  try {
    await Promise.all(Array.from({ length: CONCURRENCY }, () => uploadWorker(cola)));
    if (!cancelled.value) {
      done.value        = true;
      currentFile.value = "";
      speed.value       = "";
    }
  } catch (err) {
    // Si falla un worker se detiene al otro; una cancelación no es un fallo
    if (!cancelled.value) {
      cancelled.value = true;
      abortador.abort();
    }
    if (!esCancelacion(err)) {
      uploadError.value = "Error al subir: " + (err?.response?.data?.message || err?.message);
    }
  } finally {
    uploading.value = false;
    paused.value    = false;
  }
}

async function uploadWorker(cola) {
  while (cola.length && !cancelled.value) {
    await uploadFile(cola.shift());
    uploadedCount.value++;
  }
}

function tamanoChunk(file, index) {
  return Math.max(0, Math.min(CHUNK_SIZE, file.size - index * CHUNK_SIZE));
}

async function uploadFile(file) {
  const relativePath = rutaDe(file);
  const fileId       = obtenerFileId(file);
  const totalChunks  = Math.max(1, Math.ceil(file.size / CHUNK_SIZE)); // un archivo vacío envía 1 chunk vacío
  currentFile.value  = relativePath;

  const recibidos = new Set(await fetchReceivedChunks(fileId));

  for (let index = 0; index < totalChunks; index++) {
    if (cancelled.value) return;
    if (recibidos.has(index)) {
      uploadedBytes.value += tamanoChunk(file, index); // ya estaba en el servidor
      continue;
    }
    const start = index * CHUNK_SIZE;
    const chunk = file.slice(start, start + CHUNK_SIZE);
    await uploadChunkWithRetry(chunk, { fileId, index, totalChunks, relativePath });
  }

  guardar(claveArchivo(file), null);
}

// Suma (o resta) bytes enviados y recalcula la velocidad de esta sesión
function sumarBytes(n) {
  uploadedBytes.value += n;
  sessionBytes        += n;
  const segundos = (Date.now() - startTime) / 1000;
  if (segundos > 0) speed.value = (sessionBytes / 1024 / 1024 / segundos).toFixed(1) + " MB/s";
}

// axios cancela con CanceledError (code ERR_CANCELED); la espera de reintento, con AbortError
function esCancelacion(err) {
  return err?.code === "ERR_CANCELED" || err?.name === "AbortError" || err?.name === "CanceledError";
}

// Un 4xx no se arregla reintentando (salvo 408 y 429); la red caída y los 5xx sí
function esFatal(err) {
  const st = err?.response?.status;
  return st >= 400 && st < 500 && st !== 408 && st !== 429;
}

// Reintenta solo los fallos transitorios. El progreso avanza mientras se envía cada chunk
// (onUploadProgress); si el intento falla, se descuenta lo que se había contado.
async function uploadChunkWithRetry(chunk, { fileId, index, totalChunks, relativePath }) {
  for (let intento = 0; intento <= MAX_RETRIES; intento++) {
    let enviado = 0; // bytes de este intento ya contados en el progreso
    try {
      const formData = new FormData();
      formData.append("file",         chunk);
      formData.append("fileId",       fileId);
      formData.append("chunkIndex",   index);
      formData.append("totalChunks",  totalChunks);
      formData.append("relativePath", relativePath);

      await api.post("/upload/chunk", formData, {
        signal: abortador.signal,
        timeout: TIMEOUT_CHUNK_MS,
        onUploadProgress: e => {
          // e.loaded incluye la cabecera multipart: se limita al tamaño del chunk
          const actual = Math.min(e.loaded, chunk.size);
          sumarBytes(actual - enviado);
          enviado = actual;
        },
      });
      sumarBytes(chunk.size - enviado); // completa lo que el progreso no alcanzó a informar
      paused.value = false;
      return;
    } catch (err) {
      sumarBytes(-enviado);
      if (esCancelacion(err) || esFatal(err) || intento === MAX_RETRIES) throw err;
      paused.value = true;
      await sleep(RETRY_DELAYS[intento] ?? 24000, abortador.signal);
    }
  }
}

function cancelUpload() {
  cancelled.value   = true;
  currentFile.value = "";
  paused.value      = false;
  abortador?.abort(); // corta los envíos en curso; "Reanudar subida" continúa donde quedó
}

function reset() {
  abortador?.abort();
  files.value         = [];
  ignorados.value     = 0;
  done.value          = false;
  uploadError.value   = "";
  uploadedBytes.value = 0;
  uploadedCount.value = 0;
  speed.value         = "";
  cancelled.value     = false;
  paused.value        = false;
  currentFile.value   = "";
  // El indexador también se reinicia al cambiar la carpeta
  indexAbortador?.abort();
  ejecutando.value  = false;
  lineas.value      = [];
  estadoFinal.value = null;
  reiniciarImportacion();
}

// ── Importación (paso 3) ──────────────────────────────────────────────────────
// POST /procedimientos/importar-archivo-pla (axios): el backend ejecuta CALL carga_importar_archivo_pla(idTramite, 0)
// y devuelve los RAISE NOTICE del procedimiento ya separados en { campo, valor }.
async function ejecutarImportacion() {
  if (estadoImport.value === "COMPLETADO") {
    const r = await Swal.fire({
      icon: "question",
      title: "Importar de nuevo",
      text: "El trámite ya se importó. Volver a ejecutar el procedimiento puede duplicar registros si no limpia los anteriores.",
      showCancelButton: true,
      confirmButtonText: "Importar",
      cancelButtonText: "Cancelar",
    });
    if (!r.isConfirmed) return;
  }

  reiniciarImportacion();
  importando.value = true;
  try {
    // timeout: 0 -> sin límite de axios: el procedimiento puede tardar con CSV grandes
    const { data } = await api.post("/procedimientos/importar-archivo-pla", null, {
      params: { idTramite: idTramite.value },
      timeout: 0,
    });
    resumenImport.value = data.resumen || [];
    estadoImport.value  = "COMPLETADO";
    Swal.fire({
      icon: advertenciasImport.value.length ? "warning" : "success",
      title: advertenciasImport.value.length ? "Importación con observaciones" : "Importación completada",
      text: advertenciasImport.value.length
        ? advertenciasImport.value.join(" ")
        : `Trámite ${idTramite.value} importado correctamente`,
      confirmButtonText: "Aceptar",
    });
  } catch (error) {
    // axios lanza ante cualquier respuesta fuera de 2xx; el cuerpo trae el mensaje de BusinessException
    estadoImport.value = "ERROR";
    errorImport.value  = mensajeDeError(error);
    Swal.fire({
      icon: "error",
      title: error.response ? "Error en la importación" : "Error de conexión",
      text: errorImport.value,
      confirmButtonText: "Aceptar",
    });
  } finally {
    importando.value = false;
  }
}

// ── Indexador ─────────────────────────────────────────────────────────────────
// ── Indexador (paso 2) ────────────────────────────────────────────────────────
// El endpoint emite Server-Sent Events. EventSource no permite enviar cabeceras, así que no puede
// llevar el token JWT: se usa axios (api), cuyo interceptor ya lo añade, y el flujo se lee a mano.

// Extrae los eventos SSE completos (terminados por una línea en blanco) del texto acumulado.
// `desde` es la posición ya procesada; devuelve los eventos nuevos y la nueva posición.
function leerEventosSse(texto, desde) {
  const norm = texto.replace(/\r\n?/g, "\n");
  const fin  = norm.lastIndexOf("\n\n");
  if (fin < desde) return { eventos: [], hasta: desde };

  const eventos = [];
  norm.slice(desde, fin).split("\n\n").forEach(bloque => {
    let evento = "message";
    const datos = [];
    bloque.split("\n").forEach(linea => {
      if (!linea || linea.startsWith(":")) return; // comentario / latido
      const i     = linea.indexOf(":");
      const campo = i === -1 ? linea : linea.slice(0, i);
      let valor   = i === -1 ? "" : linea.slice(i + 1);
      if (valor.startsWith(" ")) valor = valor.slice(1);
      if (campo === "event")     evento = valor;
      else if (campo === "data") datos.push(valor);
    });
    if (datos.length) eventos.push({ evento, datos: datos.join("\n") });
  });
  return { eventos, hasta: fin + 2 };
}

async function ejecutarIndexador() {
  indexAbortador?.abort();
  const abortador   = new AbortController();
  indexAbortador    = abortador;
  lineas.value      = [];
  estadoFinal.value = null;
  ejecutando.value  = true;
  reiniciarImportacion(); // el CSV se regenera: el resumen anterior ya no vale

  let consumido  = 0;
  let finalizado = false;

  const terminar = estado => {
    finalizado        = true;
    estadoFinal.value = estado;
    ejecutando.value  = false;
    if (estado === "COMPLETADO") {
      Swal.fire({
        icon: "success",
        title: "Indexación completada",
        text: `Trámite ${idTramite.value} procesado correctamente`,
        confirmButtonText: "Aceptar",
      });
    } else {
      Swal.fire({
        icon: "error",
        title: "Error en indexación",
        text: `Ocurrió un error al procesar el trámite ${idTramite.value}`,
        confirmButtonText: "Aceptar",
      });
    }
  };

  const procesar = texto => {
    const r = leerEventosSse(texto, consumido);
    consumido = r.hasta;
    r.eventos.forEach(({ evento, datos }) => {
      if (finalizado) return;
      if (evento === "fin") {
        terminar(datos);
      } else if (evento === "error") {
        lineas.value.push(datos);
        terminar("ERROR");
      } else {
        lineas.value.push(datos);
        if (lineas.value.length > MAX_LINEAS) lineas.value.splice(0, lineas.value.length - MAX_LINEAS);
      }
    });
  };

  try {
    const res = await api.get(`/indexador/ejecutar/${idTramite.value}`, {
      responseType: "text",
      transformResponse: [d => d], // sin JSON.parse: es texto SSE
      headers: { Accept: "text/event-stream" },
      timeout: 0,                  // la indexación puede tardar minutos
      signal: abortador.signal,
      // Se llama a medida que llegan datos: el texto acumulado está en la petición XHR
      onDownloadProgress: e => {
        const xhr   = e?.event?.target || e?.target || e?.currentTarget;
        const texto = xhr?.responseText;
        if (typeof texto === "string") procesar(texto);
      },
    });
    // Eventos que el progreso no alcanzó a entregar (y el último bloque si no cerró con línea en blanco)
    procesar(String(res.data ?? "") + "\n\n");
    if (!finalizado) {
      estadoFinal.value = "ERROR";
      ejecutando.value  = false;
      Swal.fire({
        icon: "error",
        title: "Conexión interrumpida",
        text: "El servidor cerró la conexión sin informar el resultado de la indexación.",
        confirmButtonText: "Aceptar",
      });
    }
  } catch (error) {
    if (esCancelacion(error) || abortador !== indexAbortador) return; // cancelado o reemplazado por otra ejecución
    if (finalizado) return;
    estadoFinal.value = "ERROR";
    ejecutando.value  = false;
    Swal.fire({
      icon: "error",
      title: error.response ? "Error en indexación" : "Error de conexión",
      text: mensajeDeError(error),
      confirmButtonText: "Aceptar",
    });
  }
}

// La consola baja sola al llegar líneas nuevas
watch(() => lineas.value.length, async () => {
  await nextTick();
  const el = consolaRef.value;
  if (el) el.scrollTop = el.scrollHeight;
});

function claseLinea(linea) {
  if (linea.includes("Error") || linea.includes("ERROR"))     return "linea-error";
  if (linea.includes("==="))                                  return "linea-separador";
  if (linea.includes("100%") || linea.includes("terminado"))  return "linea-exito";
  return "linea-normal";
}

// ── Helpers ───────────────────────────────────────────────────────────────────
async function fetchReceivedChunks(fileId) {
  try {
    const { data } = await api.get(`/upload/chunk/status/${fileId}`, { signal: abortador?.signal });
    return data.receivedChunks || [];
  } catch (err) {
    if (esCancelacion(err)) throw err; // cancelado: no seguir con la subida
    return []; // sin saber qué hay en el servidor, se sube el archivo completo
  }
}

// El id es por trámite + ruta + tamaño + fecha: dos archivos con el mismo nombre en carpetas
// o trámites distintos no comparten chunks, y un archivo modificado empieza de cero
function identidadArchivo(file) {
  return `${idTramite.value}|${rutaDe(file)}|${file.size}|${file.lastModified}`;
}
const claveArchivo = file => "upload_" + identidadArchivo(file);

function obtenerFileId(file) {
  const clave = claveArchivo(file);
  let id = leer(clave);
  if (!id) {
    id = crypto.randomUUID();
    guardar(clave, id);
  }
  return id;
}

// localStorage puede no estar disponible (modo privado, datos bloqueados)
function leer(clave) {
  try { return localStorage.getItem(clave); } catch { return null; }
}
function guardar(clave, valor) {
  try {
    if (valor === null) localStorage.removeItem(clave);
    else localStorage.setItem(clave, valor);
  } catch { /* sin almacenamiento: la subida funciona, pero no se podrá reanudar tras recargar */ }
}

// Espera cancelable: si se cancela la subida no hay que esperar hasta 24 s
function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      clearTimeout(t);
      reject(new DOMException("Cancelado", "AbortError"));
    }, { once: true });
  });
}

function formatBytes(bytes) {
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
  const mb = bytes / 1024 / 1024;
  return mb >= 1024 ? (mb / 1024).toFixed(1) + " GB" : mb.toFixed(mb < 10 ? 1 : 0) + " MB";
}

// Escapa texto para usarlo dentro del HTML de SweetAlert
function escapar(texto) {
  return String(texto)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
</script>

<style scoped>
/* Paso bloqueado hasta que termine la subida */
.bloqueado {
  opacity: 0.5;
  pointer-events: none;
}

/* ── Consola del indexador ─────────────────────────────────────────────────── */
.consola {
  background: #1e1e1e;
  color: #d4d4d4;
  font-family: "Courier New", monospace;
  font-size: 13px;
  padding: 16px;
  border-radius: 6px;
  max-height: 320px;
  overflow-y: auto;
}
.consola p { margin: 1px 0; white-space: pre-wrap; word-break: break-word; }
.linea-normal    { color: #d4d4d4; }
.linea-separador { color: #569cd6; font-weight: bold; }
.linea-exito     { color: #4ec9b0; }
.linea-error     { color: #f44747; }
</style>